/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  TextField,
  Typography,
  Stack,
} from '@mui/material';
import PropTypes from 'prop-types';

import api from '../../../services/api';

const DEFAULT_FORM = {
  name: '',
  phone: '',
  address: '',
  number: '',
  neighborhood: '',
  city: '',
  state: '',
  cep: '',
  logo: '',
  url: '',
};

// Separa "Rua X, 123" de volta em address + number pra editar num campo cada.
const splitAddressAndNumber = (rawAddress) => {
  if (!rawAddress) return { address: '', number: '' };
  const match = rawAddress.match(/^(.+),\s*(\d+[^\s]*)$/);
  if (match) return { address: match[1].trim(), number: match[2].trim() };
  return { address: rawAddress, number: '' };
};

export default function ExpositionFormModal({
  open,
  onClose,
  expositionToEdit,
  onSaved,
}) {
  const isEditMode = !!expositionToEdit;

  const [form, setForm] = useState(DEFAULT_FORM);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setForm(DEFAULT_FORM);
      return;
    }
    if (expositionToEdit) {
      const { address, number } = splitAddressAndNumber(expositionToEdit.address || '');
      setForm({
        name: expositionToEdit.name || '',
        phone: expositionToEdit.phone || '',
        address,
        number,
        neighborhood: expositionToEdit.neighborhood || '',
        city: expositionToEdit.city || '',
        state: expositionToEdit.state || '',
        cep: expositionToEdit.cep || '',
        logo: expositionToEdit.logo || '',
        url: expositionToEdit.url || '',
      });
    }
  }, [open, expositionToEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const getAddressFromCep = async () => {
    if (!form.cep) return;
    try {
      const { data } = await axios.get(`https://viacep.com.br/ws/${form.cep}/json/`);
      if (data.erro) return;
      setForm((prev) => ({
        ...prev,
        address: data.logradouro || prev.address,
        neighborhood: data.bairro || prev.neighborhood,
        city: data.localidade || prev.city,
        state: data.uf || prev.state,
      }));
    } catch (err) {
      console.error('Erro ao buscar CEP:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      alert('Título é obrigatório.');
      return;
    }

    const data = {
      name: form.name.trim(),
      phone: form.phone,
      address: form.number ? `${form.address}, ${form.number}` : form.address,
      neighborhood: form.neighborhood,
      city: form.city,
      state: form.state,
      cep: form.cep,
      logo: form.logo,
      url: form.url,
    };

    setSubmitting(true);
    try {
      if (form.state !== '') {
        try {
          const res = await axios.get(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${form.state}`);
          if (!res.data.sigla) {
            alert('UF inválida.');
            setSubmitting(false);
            return;
          }
        } catch {
          alert('UF inválida.');
          setSubmitting(false);
          return;
        }
      }

      const saved = isEditMode
        ? (await api.patch(`/expositions/${expositionToEdit.id}`, data)).data
        : (await api.post('/expositions', data)).data;
      alert(isEditMode ? 'Exposição atualizada.' : 'Exposição cadastrada.');
      if (onSaved) onSaved(saved);
      onClose();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || `Erro ao ${isEditMode ? 'atualizar' : 'cadastrar'} exposição.`);
    } finally {
      setSubmitting(false);
    }
  };

  const title = isEditMode ? 'Editar local' : 'Novo local';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
            Dados do local físico. Preencha o CEP e o endereço é preenchido automaticamente.
          </Typography>

          <FormControl fullWidth sx={{ my: 1 }}>
            <TextField
              label="Título"
              type="text"
              name="name"
              value={form.name}
              placeholder="Nome do local"
              onChange={handleChange}
              required
              inputProps={{ maxLength: 100 }}
            />
          </FormControl>

          <FormControl fullWidth sx={{ my: 1 }}>
            <TextField
              label="Site (opcional)"
              type="url"
              name="url"
              value={form.url}
              placeholder="https://..."
              onChange={handleChange}
              inputProps={{ maxLength: 100 }}
            />
          </FormControl>

          <FormControl fullWidth sx={{ my: 1 }}>
            <TextField
              label="Telefone (opcional)"
              type="tel"
              name="phone"
              value={form.phone}
              placeholder="(11) 99999-9999"
              onChange={handleChange}
              inputProps={{ maxLength: 15 }}
            />
          </FormControl>

          <Stack direction="row" spacing={1} sx={{ my: 1 }}>
            <FormControl sx={{ flex: 3 }}>
              <TextField
                label="CEP"
                type="text"
                name="cep"
                value={form.cep}
                placeholder="00000-000"
                onChange={handleChange}
                onBlur={getAddressFromCep}
                inputProps={{ maxLength: 9 }}
                helperText="Sai daqui pra buscar automático"
              />
            </FormControl>
            <FormControl sx={{ flex: 7 }}>
              <TextField
                label="Endereço"
                type="text"
                name="address"
                value={form.address}
                placeholder="Rua X"
                onChange={handleChange}
                inputProps={{ maxLength: 50 }}
              />
            </FormControl>
          </Stack>

          <Stack direction="row" spacing={1} sx={{ my: 1 }}>
            <FormControl sx={{ flex: 3 }}>
              <TextField
                label="Número"
                type="text"
                name="number"
                value={form.number}
                placeholder="123"
                onChange={handleChange}
                inputProps={{ maxLength: 10 }}
              />
            </FormControl>
            <FormControl sx={{ flex: 7 }}>
              <TextField
                label="Bairro"
                type="text"
                name="neighborhood"
                value={form.neighborhood}
                placeholder="Centro"
                onChange={handleChange}
                inputProps={{ maxLength: 50 }}
              />
            </FormControl>
          </Stack>

          <Stack direction="row" spacing={1} sx={{ my: 1 }}>
            <FormControl sx={{ flex: 7 }}>
              <TextField
                label="Cidade"
                type="text"
                name="city"
                value={form.city}
                placeholder="São Paulo"
                onChange={handleChange}
                inputProps={{ maxLength: 50 }}
              />
            </FormControl>
            <FormControl sx={{ flex: 3 }}>
              <TextField
                label="UF"
                type="text"
                name="state"
                value={form.state}
                placeholder="SP"
                onChange={handleChange}
                onInput={(e) => { e.target.value = e.target.value.toUpperCase(); }}
                inputProps={{ maxLength: 2 }}
              />
            </FormControl>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={submitting}>Cancelar</Button>
          <Button
            type="submit"
            variant="contained"
            color="success"
            disabled={submitting}
          >
            {(() => {
              if (submitting) return 'Salvando...';
              return isEditMode ? 'Salvar alterações' : 'Cadastrar';
            })()}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

ExpositionFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
  expositionToEdit: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    phone: PropTypes.string,
    address: PropTypes.string,
    neighborhood: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    cep: PropTypes.string,
    logo: PropTypes.string,
    url: PropTypes.string,
  }),
};
